import { useState } from "react";
import type { LauraAudit } from "@/app/components/shared/types";
import { LauraStageShell } from "../shared/LauraStageShell";
import { StageSectionNav } from "../shared/StageSectionNav";
import type { P1FormData } from "./p1Types";
import { defaultP1FormData } from "./p1Types";
import { useTranslation } from "@/app/i18n/LocaleContext";
import type { LauraStageState } from "@/app/lib/auditflowApi";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

type Props = {
    audit: LauraAudit;
    deliverableContent: string;
    onDeliverableSaved: (content: string) => void;
    onStageStateChange?: (state: LauraStageState | null) => void;
};

const SECTIONS = [
    { id: "subject", key: "p1_subject" },
    { id: "criteria", key: "p1_criteria" },
    { id: "evidence", key: "p1_evidence" },
    { id: "problem", key: "p1_problem" },
    { id: "stakeholders", key: "p1_stakeholders" },
    { id: "assurance", key: "p1_assurance" },
    { id: "objective", key: "p1_objective" },
    { id: "outOfScope", key: "p1_outOfScope" },
    { id: "scope", key: "p1_scope" },
    { id: "engRisks", key: "p1_engRisks" },
    { id: "deliverables", key: "p1_deliverables" },
    { id: "timeline", key: "p1_timeline" },
    { id: "benefits", key: "p1_benefits" },
] as const;

const CRITERIA_TYPE_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
    statutory: "destructive",
    policy: "default",
    operational: "secondary",
    benchmark: "outline",
};

export function LauraP1Workspace(props: Props) {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState("subject");

    return (
        <LauraStageShell<P1FormData>
            audit={props.audit}
            stageId="p1"
            deliverableContent={props.deliverableContent}
            onDeliverableSaved={props.onDeliverableSaved}
            onStageStateChange={props.onStageStateChange}
            defaultForm={defaultP1FormData}
            title={t("laura", "p1Title")}
            description="13-component audit canvas per ISSAI 3000"
            generateLabel={t("laura", "generateCanvas")}
        >
            {({ form, setForm }) => (
                <div className="flex flex-col gap-4">
                    <StageSectionNav
                        sections={SECTIONS.map((s) => ({
                            id: s.id,
                            label: t("laura", s.key),
                        }))}
                        activeId={activeSection}
                        onSelect={setActiveSection}
                    />
                    <P1SectionContent
                        activeSection={activeSection}
                        form={form}
                        setForm={setForm}
                    />
                </div>
            )}
        </LauraStageShell>
    );
}

function P1SectionContent({
    activeSection,
    form,
    setForm,
}: {
    activeSection: string;
    form: P1FormData;
    setForm: (fn: (p: P1FormData) => P1FormData) => void;
}) {
    const { t } = useTranslation();

    if (activeSection === "subject") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_subject")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <FieldGroup>
                        <Field>
                            <FieldLabel>Subject matter</FieldLabel>
                            <Textarea
                                rows={4}
                                value={form.subject_matter}
                                onChange={(e) =>
                                    setForm((p) => ({ ...p, subject_matter: e.target.value }))
                                }
                            />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            {(["temporal", "spatial", "institutional", "functional"] as const).map(
                                (k) => (
                                    <Field key={k}>
                                        <FieldLabel className="capitalize">{k}</FieldLabel>
                                        <Input
                                            value={form.delimitation[k]}
                                            onChange={(e) =>
                                                setForm((p) => ({
                                                    ...p,
                                                    delimitation: {
                                                        ...p.delimitation,
                                                        [k]: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </Field>
                                ),
                            )}
                        </div>
                    </FieldGroup>
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "criteria") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_criteria")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Source</TableHead>
                                <TableHead>Standards</TableHead>
                                <TableHead>Type</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {form.criteria.map((c, i) => (
                                <TableRow key={c.code || i}>
                                    <TableCell className="font-mono text-xs">{c.code}</TableCell>
                                    <TableCell>{c.source}</TableCell>
                                    <TableCell className="max-w-xs truncate">{c.standards}</TableCell>
                                    <TableCell>
                                        <Badge variant={CRITERIA_TYPE_VARIANT[c.type] ?? "secondary"}>
                                            {c.type}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "stakeholders") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_stakeholders")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <StakeholderMatrix
                        stakeholders={form.stakeholders}
                        onChange={(stakeholders) =>
                            setForm((p) => ({ ...p, stakeholders }))
                        }
                    />
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "objective") {
        return (
            <Card className="border-l-4 border-l-primary">
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_objective")}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-3">
                    <Textarea
                        rows={5}
                        className="text-sm"
                        value={form.objective}
                        onChange={(e) =>
                            setForm((p) => ({ ...p, objective: e.target.value }))
                        }
                    />
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>SMART</TableHead>
                                <TableHead>Met</TableHead>
                                <TableHead>Basis</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {form.smart_checks.map((row, i) => (
                                <TableRow key={row.criterion}>
                                    <TableCell>{row.criterion}</TableCell>
                                    <TableCell>
                                        <input
                                            type="checkbox"
                                            checked={row.met}
                                            onChange={(e) => {
                                                const checks = [...form.smart_checks];
                                                checks[i] = { ...row, met: e.target.checked };
                                                setForm((p) => ({ ...p, smart_checks: checks }));
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            className="h-8 text-xs"
                                            value={row.basis}
                                            onChange={(e) => {
                                                const checks = [...form.smart_checks];
                                                checks[i] = { ...row, basis: e.target.value };
                                                setForm((p) => ({ ...p, smart_checks: checks }));
                                            }}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "engRisks") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_engRisks")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Code</TableHead>
                                <TableHead>Risk</TableHead>
                                <TableHead>Level</TableHead>
                                <TableHead>Response</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {form.engagement_risks.map((r) => (
                                <TableRow key={r.code}>
                                    <TableCell className="font-mono text-xs">{r.code}</TableCell>
                                    <TableCell>{r.risk}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                r.level.toLowerCase().includes("high")
                                                    ? "destructive"
                                                    : "secondary"
                                            }
                                        >
                                            {r.level}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-xs">{r.response}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "deliverables") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_deliverables")}</CardTitle>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Phase</TableHead>
                                <TableHead>Stage</TableHead>
                                <TableHead>Deliverable</TableHead>
                                <TableHead>Status</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {form.deliverables_tracker.map((d, i) => (
                                <TableRow key={i}>
                                    <TableCell>{d.phase}</TableCell>
                                    <TableCell className="font-mono">{d.stage}</TableCell>
                                    <TableCell>{d.deliverable}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={
                                                d.status === "complete" ? "default" : "outline"
                                            }
                                        >
                                            {d.status}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "timeline") {
        return (
            <Card>
                <CardHeader>
                    <CardTitle className="text-sm">{t("laura", "p1_timeline")}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                    {form.timeline.map((m, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                            <div className="h-2 w-2 rounded-full bg-primary" />
                            <span className="flex-1">{m.milestone}</span>
                            <Badge variant="outline">{m.target}</Badge>
                        </div>
                    ))}
                    {form.process_flow.length > 0 && (
                        <div className="mt-3 flex flex-wrap items-center gap-1 text-xs">
                            {form.process_flow.map((step, i) => (
                                <span key={step} className="flex items-center gap-1">
                                    <span className="rounded border px-2 py-0.5">{step}</span>
                                    {i < form.process_flow.length - 1 && (
                                        <span className="text-muted-foreground">→</span>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        );
    }

    if (activeSection === "benefits") {
        return (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">{t("laura", "financialBenefits")}</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                        {form.benefits_financial.map((b, i) => (
                            <div key={i} className="rounded-md border p-2 text-xs">
                                <p>{b.description}</p>
                                <Badge variant="outline" className="mt-1">
                                    {b.value || "TBD"}
                                </Badge>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="text-sm">
                            {t("laura", "nonFinancialBenefits")}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                        {form.benefits_non_financial.map((b, i) => (
                            <div key={i} className="rounded-md border p-2 text-xs">
                                <p>{b.description}</p>
                                <Badge variant="secondary" className="mt-1">
                                    {b.value}
                                </Badge>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            </div>
        );
    }

    const textSections: Record<string, keyof P1FormData> = {
        evidence: "evidence_strategy" as keyof P1FormData,
        problem: "audit_problem",
        assurance: "assurance_justification",
        outOfScope: "out_of_scope" as keyof P1FormData,
        scope: "scope" as keyof P1FormData,
    };

    const sectionKey = textSections[activeSection];
    if (!sectionKey) return null;

    const sectionLabel = SECTIONS.find((s) => s.id === activeSection)?.key;
    const value =
        activeSection === "outOfScope"
            ? form.out_of_scope.join("\n")
            : activeSection === "problem"
              ? form.audit_problem
              : activeSection === "assurance"
                ? form.assurance_justification
                : "";

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm">
                    {sectionLabel ? t("laura", sectionLabel) : activeSection}
                </CardTitle>
            </CardHeader>
            <CardContent>
                <Textarea
                    rows={6}
                    value={value}
                    onChange={(e) => {
                        const v = e.target.value;
                        if (activeSection === "outOfScope") {
                            setForm((p) => ({
                                ...p,
                                out_of_scope: v.split("\n").filter(Boolean),
                            }));
                        } else if (activeSection === "problem") {
                            setForm((p) => ({ ...p, audit_problem: v }));
                        } else if (activeSection === "assurance") {
                            setForm((p) => ({
                                ...p,
                                assurance_justification: v,
                                assurance_level: "reasonable",
                            }));
                        }
                    }}
                />
            </CardContent>
        </Card>
    );
}

function StakeholderMatrix({
    stakeholders,
    onChange,
}: {
    stakeholders: P1FormData["stakeholders"];
    onChange: (s: P1FormData["stakeholders"]) => void;
}) {
    const quadrants = [
        {
            key: "hi-hi",
            label: "High interest · High influence",
            filter: (s: (typeof stakeholders)[0]) =>
                s.interest_level === "high" && s.influence === "high",
            className: "bg-primary/5",
        },
        {
            key: "hi-lo",
            label: "High interest · Low influence",
            filter: (s: (typeof stakeholders)[0]) =>
                s.interest_level === "high" && s.influence === "low",
            className: "bg-muted/50",
        },
        {
            key: "lo-hi",
            label: "Low interest · High influence",
            filter: (s: (typeof stakeholders)[0]) =>
                s.interest_level === "low" && s.influence === "high",
            className: "bg-muted/50",
        },
        {
            key: "lo-lo",
            label: "Low interest · Low influence",
            filter: (s: (typeof stakeholders)[0]) =>
                s.interest_level === "low" && s.influence === "low",
            className: "bg-muted/30",
        },
    ];

    return (
        <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
                {quadrants.map((q) => (
                    <div
                        key={q.key}
                        className={cn(
                            "min-h-20 rounded-lg border border-dashed p-2",
                            q.className,
                        )}
                    >
                        <p className="text-[10px] text-muted-foreground">{q.label}</p>
                        <div className="mt-1 flex flex-wrap gap-1">
                            {stakeholders.filter(q.filter).map((s) => (
                                <Badge key={s.name} variant="secondary">
                                    {s.name}
                                </Badge>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <Textarea
                rows={4}
                className="font-mono text-xs"
                placeholder="name|interest high/low|influence high/low|relevance"
                value={stakeholders
                    .map(
                        (s) =>
                            `${s.name}|${s.interest_level}|${s.influence}|${s.relevance}`,
                    )
                    .join("\n")}
                onChange={(e) =>
                    onChange(
                        e.target.value
                            .split("\n")
                            .filter(Boolean)
                            .map((line) => {
                                const [name, il, inf, relevance] = line.split("|");
                                return {
                                    name: name ?? "",
                                    interest: "",
                                    interest_level: il === "high" ? "high" : "low",
                                    influence: inf === "high" ? "high" : "low",
                                    relevance: relevance ?? "",
                                };
                            }),
                    )
                }
            />
        </div>
    );
}
